import { useMemo } from 'react';

import { FIELD_CONFIG } from 'configs/columns';

import { isEnumMember } from 'utils/typeUtils';

import { useQuery } from '@apollo/client';

import { FilterableField, LoanField } from '__generated__/globalTypes';
import { GetValidTerms, GetValidTermsVariables } from 'query/__generated__/getValidTerms';
import { GET_VALID_TERMS } from 'query/getValidTerms';

import { FilterInputType } from './FilterInputs';

import { GetFilterBounds_deal_performance_summary } from 'features/core/filter/FilterInputs';
export type CarveFilterConfig = {
  labelAboveFilter: string;
  labelInDropdown: string;
  type: FilterInputType;
  step?: number;
  valueFormatter?: (value: number | undefined) => string;
  // Inverse of valueFormatter, for the range slider's typed-value boxes —
  // parses what a user types (the *displayed* unit, e.g. "25.00%") back
  // into the field's raw unit (bps/cents) the slider actually operates in.
  // Only meaningful when valueFormatter converts units; a field displayed
  // in its own raw unit (e.g. borrower_credit_score) doesn't need one.
  inputFormat?: (value: string) => number;
  minBoundKey?: keyof GetFilterBounds_deal_performance_summary;
  maxBoundKey?: keyof GetFilterBounds_deal_performance_summary;
  integer?: boolean;
  filterOptions?: {
    label: string;
    value: string;
  }[];
};

const percentStringToBps = (value: string): number => {
  const percent = parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Math.round(percent * 100);
};

const currencyStringToCents = (value: string): number => {
  const dollars = parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Math.round(dollars * 100);
};

export const carveFilterConfigs: {
  [key in FilterableField]?: CarveFilterConfig;
} = {
  /* TODO

    min_maturity_date: Date | null;
    min_origination_date: Date | null;
    min_servicing_rate: number;
  */
  [FilterableField.account_id]: {
    labelAboveFilter: 'Set Account IDs',
    labelInDropdown: FIELD_CONFIG.account_id.string,
    type: 'list-input',
  },
  [FilterableField.current_balance_cents]: {
    labelAboveFilter: 'Define Current Balance Range',
    labelInDropdown: FIELD_CONFIG.current_balance_cents.string,
    type: 'range',
    minBoundKey: 'min_current_balance_cents',
    maxBoundKey: 'max_current_balance_cents',
    // valueFormatter: FIELD_CONFIG.current_balance_cents.display,
    valueFormatter: (value) => '$' + (value ? value / 100 : '').toLocaleString(),
    inputFormat: currencyStringToCents,
    step: 10000,
  },

  [FilterableField.dti_bps]: {
    labelAboveFilter: 'Define DTI Range',
    labelInDropdown: FIELD_CONFIG.dti_bps.string,
    type: 'range',
    step: 100,
    valueFormatter: FIELD_CONFIG[LoanField.dti_bps].display,
    inputFormat: percentStringToBps,
    minBoundKey: 'min_dti_bps',
    maxBoundKey: 'max_dti_bps',
  },
  [FilterableField.ltv_bps]: {
    labelAboveFilter: 'Define LTV Range',
    labelInDropdown: FIELD_CONFIG.ltv_bps.string,
    type: 'range',
    step: 100,
    valueFormatter: FIELD_CONFIG.ltv_bps.display,
    inputFormat: percentStringToBps,
    minBoundKey: 'min_ltv_bps',
    maxBoundKey: 'max_ltv_bps',
  },
  [FilterableField.borrower_credit_score]: {
    labelAboveFilter: 'Define FICO Range',
    labelInDropdown: FIELD_CONFIG.borrower_credit_score.string,
    type: 'range',
    minBoundKey: 'min_borrower_credit_score',
    maxBoundKey: 'max_borrower_credit_score',
    integer: true,
  },
  [FilterableField.interest_rate_bps]: {
    labelAboveFilter: 'Define Coupon Range',
    labelInDropdown: FIELD_CONFIG.interest_rate_bps.string,
    type: 'range',
    step: 25,
    valueFormatter: FIELD_CONFIG.interest_rate_bps.display,
    inputFormat: percentStringToBps,
    minBoundKey: 'min_coupon_bps',
    maxBoundKey: 'max_coupon_bps',
  },
  [FilterableField.occupancy]: {
    labelAboveFilter: 'Choose Occupancy',
    labelInDropdown: FIELD_CONFIG.occupancy.string,
    type: 'select-multi',
    filterOptions: [],
  },
  [FilterableField.product]: {
    labelAboveFilter: 'Choose Products',
    labelInDropdown: FIELD_CONFIG.product.string,
    type: 'select-multi',
    filterOptions: [],
  },
  [FilterableField.purpose]: {
    labelAboveFilter: 'Choose Purposes',
    labelInDropdown: FIELD_CONFIG.purpose.string,
    type: 'select-multi',
    filterOptions: [],
  },
  [FilterableField.state]: {
    labelAboveFilter: 'Define States',
    labelInDropdown: FIELD_CONFIG.state.string,
    type: 'select-multi',
    filterOptions: [],
  },
  [FilterableField.loan_terms_months]: {
    labelAboveFilter: 'Define Loan Terms Range (Months)',
    labelInDropdown: FIELD_CONFIG.loan_terms.string,
    type: 'range',
    // Borrowed bounds: this filters original term, but PerformanceSummary exposes no
    // min/max_loan_terms_months to bound it with, so the slider's ends come from
    // remaining term and can sit inside the real range. Tracked in ENG-3678; do not
    // copy this pattern.
    minBoundKey: 'min_remaining_loan_terms_months',
    maxBoundKey: 'max_remaining_loan_terms_months',
    // No step was ever set for this field, but the old rc-slider-based
    // component's own default step is 1 — so this was always a whole-month
    // range in practice. Making that explicit here rather than relying on
    // RangeSlider's fallback (which rounds to the nearest quarter-unit when
    // neither step/integer/decimal is set) — the old and new "no step
    // configured" defaults aren't the same, and a loan term of "14.25
    // months" is meaningless.
    integer: true,
  },
  [FilterableField.remaining_loan_terms_months]: {
    labelAboveFilter: 'Define Maturity Range (Months)',
    labelInDropdown: FIELD_CONFIG.remaining_loan_terms.string,
    type: 'range',
    minBoundKey: 'min_remaining_loan_terms_months',
    maxBoundKey: 'max_remaining_loan_terms_months',
    // Whole months, as with the other month-range criteria.
    integer: true,
  },
  [FilterableField.age_months]: {
    labelAboveFilter: 'Define Age Range (Months)',
    labelInDropdown: FIELD_CONFIG.age_months.string,
    type: 'range',
    minBoundKey: 'min_age_months',
    maxBoundKey: 'max_age_months',
    // Same as loan_terms_months above: rc-slider's implicit step:1 default,
    // made explicit for RangeSlider.
    integer: true,
  },
};

function useCarveFilterConfig(input: Omit<GetValidTermsVariables['input'], 'fieldNames'>) {
  const filterConfig = useMemo(() => carveFilterConfigs, []);

  const termFieldNames = useMemo(
    () =>
      Object.entries(filterConfig)
        .filter(([_fieldName, filter]) => filter.type === 'select-multi')
        .map(([fieldName]) => fieldName)
        .filter(isEnumMember(FilterableField)),
    [filterConfig],
  );

  const { data: validTermsData } = useQuery<GetValidTerms, GetValidTermsVariables>(
    GET_VALID_TERMS,
    {
      variables: {
        input: {
          ...input,
          fieldNames: termFieldNames as FilterableField[],
        },
      },
      skip: termFieldNames.length === 0,
    },
  );

  const updatedFilterConfig = useMemo(() => {
    if (!validTermsData) {
      return filterConfig;
    }

    const filtersConfig = { ...filterConfig };

    Object.entries(filtersConfig).forEach(([fieldName, filter]) => {
      if (filter.type === 'select-multi' && isEnumMember(FilterableField)(fieldName)) {
        const validTerms = validTermsData.getValidTerms.find(
          (terms) => terms.fieldName === fieldName,
        );
        if (validTerms) {
          filter.filterOptions = validTerms.terms
            .map((term) => ({
              label: term,
              value: term,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
        }
      }
    });

    return filtersConfig;
  }, [filterConfig, validTermsData]);

  return updatedFilterConfig;
}

export { useCarveFilterConfig };
